//@ts-strict-ignore
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { Prompt, useHistory } from 'react-router';
import { Checkbox, Dialog, DialogType } from '@fluentui/react';
import { Button, SuccessButton } from './Button';
import { DialogBottomButtonContainer } from '../modals/Styles';
import { useCookies } from 'react-cookie';
import { add } from 'date-fns';
import { Analytics } from '../../mixpanel';
import { Action } from 'history';

const DISABLE_LEAVE_FORM_PROMPT = 'DISABLE_LEAVE_FORM_PROMPT';

export const ConnectedFormPristinePrompt = ({ pristine: pristineFromProps }: { pristine?: boolean }) => {
  const { name, pristine: pristineFromConnectedForm } = useContext(ConnectedFormContext);

  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [lastAction, setLastAction] = useState<Action | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [cookies, setCookie] = useCookies([DISABLE_LEAVE_FORM_PROMPT]);
  const hasTracked = useRef(false);

  const history = useHistory();

  const nameLowerCase = (name || '').toLowerCase();

  //Note - It is important for this to be in an effect, because if we try to navigate directly off clicking the confirm button,
  //       then the Prompt component will block navigation (So you'll then have to click confirm a second time).
  useEffect(() => {
    if (allowNavigation && lastLocation?.pathname) {
      if (isChecked) {
        //Obey instruction for 1 month only.
        setCookie(DISABLE_LEAVE_FORM_PROMPT, 'true', { expires: add(new Date(), { months: 1 }) });

        Analytics.track('Navigation warning ignored', {
          formName: nameLowerCase,
        });
      }

      if (lastAction === 'POP') {
        history.goBack();
      } else {
        history.push({
          pathname: lastLocation.pathname,
          state: { previousLocation: history.location?.pathname || 'back' },
        });
      }
    }
  }, [allowNavigation, history, isChecked, lastAction, lastLocation?.pathname, nameLowerCase, setCookie]);

  useEffect(() => {
    if (modalOpen && !hasTracked.current) {
      Analytics.track('Navigation warning displayed', {
        formName: nameLowerCase,
      });
      hasTracked.current = true;
    }
  }, [modalOpen, nameLowerCase]);

  const onChange = useCallback((ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void => {
    setIsChecked(!!checked);
  }, []);

  //If the cookie is set, don't block navigation.
  if (cookies[DISABLE_LEAVE_FORM_PROMPT] === 'true') {
    return null;
  }

  const pristine = pristineFromProps !== undefined ? pristineFromProps : pristineFromConnectedForm;

  return (
    <>
      <Prompt
        when={!pristine}
        message={(location, action) => {
          if (!allowNavigation) {
            setModalOpen(true);
            setLastAction(action);
            setLastLocation(location);
            return false;
          }
          return true;
        }}
      />
      <Dialog
        hidden={!modalOpen}
        dialogContentProps={{
          type: DialogType.normal,
          title: `Leave without saving this ${nameLowerCase}`,
          closeButtonAriaLabel: 'Navigate',
          subText: `You have some unsaved changes in your ${nameLowerCase}. If you leave now, your changes will be lost.`,
        }}
        minWidth={'450px'}
      >
        <Checkbox
          label="Don't show this again."
          checked={isChecked}
          onChange={onChange}
          styles={{
            root: {
              paddingBottom: '14px',
            },
            text: {
              color: 'grey',
            },
          }}
        />
        <DialogBottomButtonContainer>
          <Button
            data-testid="allow-navigation-cancel"
            onClick={() => {
              setModalOpen(false);
              setLastLocation(null);
            }}
          >
            Stay here
          </Button>
          <SuccessButton
            data-testid="allow-navigation-confirm"
            onClick={() => {
              setModalOpen(false);
              setAllowNavigation(true);
            }}
          >
            Leave without saving
          </SuccessButton>
        </DialogBottomButtonContainer>
      </Dialog>
    </>
  );
};
